import { useEffect, useState } from 'react'

import { Dialog } from '@mui/material'

import MDBox from 'components/MDBox'

import { emptyPayphoneLocation, PayphoneLocation } from '../types'
import ConfirmDeleteDialog from './ConfirmDeleteDialog'
import PayphoneForm from './PayphoneForm'
import PayphoneMap from './PayphoneMap'
import PayphoneModalHeader from './PayphoneModalHeader'

interface Props {
	open: boolean
	initialValue: PayphoneLocation | null
	defaultLat: number
	defaultLng: number
	onClose: () => void
	onSave: (location: PayphoneLocation) => Promise<void>
	onDelete?: (id: string) => Promise<void>
}

function PayphoneModal({
	open,
	initialValue,
	defaultLat,
	defaultLng,
	onClose,
	onSave,
	onDelete,
}: Props): JSX.Element {
	const [form, setForm] = useState<PayphoneLocation>(emptyPayphoneLocation(defaultLat, defaultLng))
	const [saving, setSaving] = useState(false)
	const [deleting, setDeleting] = useState(false)
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

	const isEdit = Boolean(initialValue?.id)

	useEffect(() => {
		if (!open) return
		setForm(
			initialValue
				? {
						...initialValue,
						phone_numbers:
							initialValue.phone_numbers?.length > 0 ? [...initialValue.phone_numbers] : [''],
				  }
				: emptyPayphoneLocation(defaultLat, defaultLng)
		)
	}, [open, initialValue, defaultLat, defaultLng])

	const handleSave = async () => {
		setSaving(true)
		try {
			await onSave(form)
			onClose()
		} finally {
			setSaving(false)
		}
	}

	const handleDelete = async () => {
		if (!form.id || !onDelete) return
		setDeleteDialogOpen(false)
		setDeleting(true)
		try {
			await onDelete(form.id)
			onClose()
		} finally {
			setDeleting(false)
		}
	}

	return (
		<>
			<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
				<MDBox p={3} style={{ backgroundColor: '#fff' }}>
					<PayphoneModalHeader
						isEdit={isEdit}
						canSave={Boolean(form.name.trim())}
						saving={saving}
						deleting={deleting}
						onSave={handleSave}
						onDelete={() => setDeleteDialogOpen(true)}
					/>
					<PayphoneForm form={form} setForm={setForm} />
					<MDBox mt={2}>
						<PayphoneMap
							lat={Number(form.location.geometry.lat)}
							lng={Number(form.location.geometry.lng)}
							resetKey={open}
							onGeometryChange={(lat, lng) =>
								setForm(prev => ({
									...prev,
									location: {
										...prev.location,
										geometry: { lat, lng },
									},
								}))
							}
						/>
					</MDBox>
				</MDBox>
			</Dialog>
			<ConfirmDeleteDialog
				open={deleteDialogOpen}
				onClose={() => setDeleteDialogOpen(false)}
				onConfirm={handleDelete}
			/>
		</>
	)
}

export default PayphoneModal
